import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { subscribedPortfolioService } from '../../../../api';
import { TREASURY_PORTFOLIO_OWNER_TYPE } from '../../../../constants/treasury';
import { SubscribedPortfolio } from '../../../../types/subscribedPortfolio';

/**
 * The company's treasury portfolios — the owner user's subscribed portfolios with owner type `employee_treasury`
 * (the owner's own personal portfolios share the user id, so only the owner type tells them apart).
 */
export const useTreasuryPortfolios = (ownerUserId: number | null) => {
  const { enqueueSnackbar } = useSnackbar();
  const [portfolios, setPortfolios] = useState<SubscribedPortfolio[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!ownerUserId) {
      setPortfolios([]);
      return;
    }

    setIsLoading(true);

    try {
      const response = await subscribedPortfolioService.fetchSubscribedPortfolios({
        limit: 100,
        offset: 0,
        sortField: 'id',
        sortOrder: 'desc',
        filter: JSON.stringify({ items: [{ columnField: 'userId', operatorValue: '=', value: ownerUserId }] }),
      });
      const items = response.data?.items ?? [];
      setPortfolios(items.filter((portfolio) => portfolio.ownerType?.name === TREASURY_PORTFOLIO_OWNER_TYPE));
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load the treasury portfolios', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [ownerUserId, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  return { portfolios, isLoading, reload: load };
};
